import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { actions } from '@/lib/actions';
import { api, ApiError, errorMessage } from '@/lib/api';
import { createStore, useStore } from '@/lib/store';
import { daysFromToday, formatBytes, formatDay, formatMoney, formatTime } from '@/lib/format';
import { toast, toastError } from '@/lib/toast';
import { AvatarStack } from '@/ui/Avatar';
import { Icon } from '@/ui/Icon';
import { Callout, Chip, Empty, Skeleton, Spinner } from '@/ui/bits';
import {
  aiFieldWord,
  CALL_OUTCOMES,
  fileType,
  firstName,
  listWords,
  modelName,
  outcomePhrase,
  type EmailSummary,
  type MeetingItem,
  type NoteItem,
  type RecordSheet,
  type TimelineItem,
} from '@shared/record';
import { OPENS_EXPLAINED } from '@shared/mail';
import type { IconName } from '@/ui/icons';
import { dropTimeline, keys, useTimeline } from './queries';
import { cardTime, dayHeading, first, MentionText, shake } from './util';

// The record's timeline: notes, calls, emails, meetings, comments, files, deal moves and follow-ups, newest first,
// grouped by day, with upcoming meetings pinned at the top.

/** Items written in this session, which slide in once. */
export const freshStore = createStore<{ ids: string[] }>({ ids: [] });
export function markFresh(id: string): void {
  freshStore.set((s) => ({ ids: [...s.ids, id] }));
  setTimeout(() => freshStore.set((s) => ({ ids: s.ids.filter((x) => x !== id) })), 1200);
}

export const DURATIONS = [5, 10, 15, 20, 30, 45, 60, 90];

function Card({
  icon,
  tone,
  card,
  t1,
  t2,
  t3,
  itemId,
  fresh,
  acts,
}: {
  icon: IconName;
  tone?: string;
  card?: string;
  t1: ReactNode;
  t2?: ReactNode;
  t3?: ReactNode;
  itemId: string;
  fresh: boolean;
  acts?: ReactNode;
}) {
  return (
    <div className="tl-item" data-item={itemId}>
      <span className={['tl-ic', tone].filter(Boolean).join(' ')} aria-hidden="true">
        <Icon name={icon} />
      </span>
      <div className={['card', 'tl-card', card, fresh ? 'new' : ''].filter(Boolean).join(' ')}>
        <div className="t1">
          {t1}
          {acts ? <span className="tl-acts">{acts}</span> : null}
        </div>
        {t2 ? <div className="t2">{t2}</div> : null}
        {t3 ? <div className="t3">{t3}</div> : null}
      </div>
    </div>
  );
}

function Time({ at, children }: { at: string; children?: ReactNode }) {
  return <time dateTime={at}>{children ?? cardTime(at)}</time>;
}

/* ---------- notes and calls ---------- */

function NoteEditor({ note, sheet, onDone }: { note: NoteItem; sheet: RecordSheet; onDone: () => void }) {
  const qc = useQueryClient();
  const [body, setBody] = useState(note.body);
  const [outcome, setOutcome] = useState(note.callOutcome ?? 'connected');
  const [minutes, setMinutes] = useState(note.callDurationMin ?? 15);
  const [expected, setExpected] = useState(note.updatedAt);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const errId = useId();
  const call = note.kind === 'call';

  useEffect(() => {
    const el = ref.current;
    el?.focus();
    el?.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const save = async () => {
    const text = body.trim();
    if (!text && !call) {
      shake(ref.current);
      setError('Write the note, or delete it instead.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch<NoteItem>(`/notes/${note.id}`, {
        body: text,
        expectedUpdatedAt: expected,
        ...(call ? { callOutcome: outcome, callDurationMin: minutes } : {}),
      });
      void qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) });
      toast(call ? 'Call updated' : 'Note updated', { icon: 'check-circle' });
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'stale') {
        // Someone saved in between: keep this text and let the next save replace theirs knowingly.
        const latest = await api.get<NoteItem>(`/notes/${note.id}`).catch(() => null);
        if (latest) setExpected(latest.updatedAt);
        setError(`${latest?.author ? firstName(latest.author.name) : 'Someone'} changed this a moment ago. Their text is now on the timeline; save again to replace it with yours.`);
        void qc.invalidateQueries({ queryKey: keys.timeline(sheet.type, sheet.id) });
      } else {
        setError(err instanceof ApiError ? (Object.values(err.fields)[0] ?? err.message) : errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rcomp note-edit"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onDone();
        }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          void save();
        }
      }}
    >
      <div className="ta-wrap">
        <textarea
          ref={ref}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          aria-label={call ? 'What you talked about' : 'Note'}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errId : undefined}
        />
      </div>
      {error ? (
        <p className="field-err" id={errId} role="alert">
          <Icon name="warn" className="i-xs" />
          {error}
        </p>
      ) : null}
      <div className="extras">
        {call ? (
          <>
            <select className="select select-sm" aria-label="Duration" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {[...new Set([...DURATIONS, minutes])].sort((a, b) => a - b).map((m) => (
                <option key={m} value={m}>
                  {m} min
                </option>
              ))}
            </select>
            <select className="select select-sm" aria-label="Outcome" value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
              {CALL_OUTCOMES.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </>
        ) : (
          <span className="hint">Esc to cancel</span>
        )}
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-ghost" onClick={onDone}>
          Cancel
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={() => void save()} disabled={busy}>
          {busy ? <Spinner label="Saving" /> : null}
          Save
        </button>
      </div>
    </div>
  );
}

function NoteCard({ item, note, sheet, fresh }: { item: TimelineItem; note: NoteItem; sheet: RecordSheet; fresh: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const call = note.kind === 'call';
  const pending = note.id.startsWith('tmp-');
  const author = first(note.author, 'A former teammate');
  const about = sheet.type === 'company' && note.person ? firstName(note.person.name) : null;
  const t1 = call
    ? `${note.callDirection === 'in' ? 'Incoming call' : 'Call'}${about ? ` with ${about}` : ''} logged by ${author}`
    : `Note${about ? ` about ${about}` : ''} by ${author}`;
  const who = [
    call && note.callDurationMin ? `${note.callDurationMin} min` : null,
    call ? outcomePhrase(note.callOutcome) : null,
    note.deal?.name ?? null,
  ]
    .filter(Boolean)
    .join(' · ');

  const remove = async () => {
    try {
      const res = await api.post<{ ids: string[] }>('/bin', { type: 'note', ids: [note.id] });
      dropTimeline(qc, sheet.type, sheet.id, item.id);
      toast(call ? 'Call deleted' : 'Note deleted', {
        icon: 'trash',
        undo: () => {
          api
            .post('/bin/restore', { type: 'note', ids: res.ids })
            .then(() => qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) }))
            .catch((err) => toastError(err));
        },
      });
    } catch (err) {
      toastError(err);
    }
  };

  const label = call ? 'call' : 'note';
  return (
    <Card
      itemId={item.id}
      fresh={fresh}
      icon={call ? 'phone' : 'note'}
      tone={call ? 'call' : undefined}
      card={pending ? 'pending' : undefined}
      t1={
        <>
          <b>{t1}</b>
          {who ? <span className="who">{who}</span> : null}
          <Time at={note.occurredAt}>{pending ? 'Saving…' : undefined}</Time>
        </>
      }
      acts={
        pending || editing ? null : (
          <>
            <button type="button" className="icon-btn xs" aria-label={`Edit ${label}`} data-tip="Edit" onClick={() => setEditing(true)}>
              <Icon name="edit" />
            </button>
            <button type="button" className="icon-btn xs" aria-label={`Delete ${label}`} data-tip="Delete" onClick={() => void remove()}>
              <Icon name="trash" />
            </button>
          </>
        )
      }
      t2={editing ? <NoteEditor note={note} sheet={sheet} onDone={() => setEditing(false)} /> : note.body ? <span className="pre">{note.body}</span> : null}
    />
  );
}

/* ---------- emails and meetings ---------- */

function OpenHint({ email }: { email: EmailSummary }) {
  if (email.direction !== 'out' || !email.trackOpens) return null;
  if (email.openCount === 0)
    return (
      <span className="hint">
        <Icon name="eye" />
        Not opened yet
      </span>
    );
  return (
    <span className="chip chip-info" data-tip={OPENS_EXPLAINED}>
      <Icon name="eye" />
      Opened {email.openCount}× · probably
    </span>
  );
}

function EmailCard({ item, email, sheet, fresh }: { item: TimelineItem; email: EmailSummary; sheet: RecordSheet; fresh: boolean }) {
  const full = sheet.type === 'company';
  const nameOf = (n: string | null | undefined, addr: string) => (n ? (full ? n : firstName(n)) : addr);
  const from =
    email.direction === 'out' ? nameOf(email.sentBy?.name ?? email.fromName, email.fromAddr) : nameOf(email.person?.name ?? email.fromName, email.fromAddr);
  const to = email.to[0] ? nameOf(email.to[0].name, email.to[0].email) : null;
  const others = email.to.length > 1 ? ` and ${email.to.length - 1} more` : '';
  const who = [to ? `to ${to}${others}` : null, email.messageCount > 1 ? `${email.messageCount} messages` : null].filter(Boolean).join(' · ');
  return (
    <Card
      itemId={item.id}
      fresh={fresh}
      icon={email.direction === 'out' ? 'send' : 'mail'}
      tone="email"
      t1={
        <>
          <b>Email from {from}</b>
          {who ? <span className="who">{who}</span> : null}
          <Time at={item.at} />
        </>
      }
      t2={
        <>
          <b className="subj">{email.subject || '(no subject)'}</b>
          {email.snippet ? (
            <>
              <br />“{email.snippet}”
            </>
          ) : null}
        </>
      }
      t3={
        <>
          {email.direction === 'in' ? (
            <button type="button" className="btn btn-sm btn-tile" onClick={() => actions.compose({ mode: 'reply', threadId: email.threadId, emailId: email.emailId })}>
              <Icon name="reply" />
              Reply
            </button>
          ) : null}
          <OpenHint email={email} />
          {email.attachments.slice(0, 2).map((a) => {
            const t = fileType(a.name, a.mime);
            return (
              <span key={a.name} className="attach slim">
                <span className={`fi ${t.tone}`}>{t.label}</span>
                <span>
                  <b>{a.name}</b>
                </span>
              </span>
            );
          })}
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => actions.openThread(email.threadId)}>
            Open thread
          </button>
        </>
      }
    />
  );
}

/** "Now", "In 48 min", "In 3 h" within the next 12 hours, then "Tomorrow" or "In 5 days" by calendar day. */
export function startsIn(m: MeetingItem, now = Date.now()): string {
  const start = new Date(m.startsAt).getTime();
  const end = new Date(m.endsAt).getTime();
  if (start <= now && end > now) return 'Now';
  const mins = Math.round((start - now) / 60_000);
  if (mins < 60) return `In ${Math.max(1, mins)} min`;
  const days = daysFromToday(m.startsAt, new Date(now));
  if (mins < 12 * 60 || days <= 0) return `In ${Math.round(mins / 60)} h`;
  return days === 1 ? 'Tomorrow' : `In ${days} days`;
}

/** Join links show from 12 hours before a meeting until it ends. */
function joinable(m: MeetingItem, now = Date.now()): boolean {
  return new Date(m.startsAt).getTime() - now < 12 * 3_600_000 && new Date(m.endsAt).getTime() > now;
}

export function meetingTimes(m: MeetingItem): string {
  return m.allDay ? 'All day' : `${formatTime(m.startsAt)} to ${formatTime(m.endsAt)}`;
}

function MeetingCard({ item, meeting, fresh }: { item: TimelineItem; meeting: MeetingItem; fresh: boolean }) {
  const upcoming = new Date(meeting.endsAt).getTime() > Date.now();
  const people = meeting.attendees.map((a) => a.name ?? a.email);
  const where = ['Google Calendar', meeting.meetUrl ? 'Google Meet' : meeting.location].filter(Boolean).join(' · ');
  return (
    <Card
      itemId={item.id}
      fresh={fresh}
      icon="video"
      tone="meet"
      card={upcoming ? 'meet upcoming' : 'meet'}
      t1={
        <>
          <b>{meeting.title || 'Meeting'}</b>
          <span className="who">{where}</span>
          <Time at={meeting.startsAt}>{meetingTimes(meeting)}</Time>
        </>
      }
      t2={people.length ? listWords(people.slice(0, 4).concat(people.length > 4 ? [`${people.length - 4} more`] : [])) : null}
      t3={
        upcoming || meeting.htmlLink ? (
          <>
            {meeting.attendees.length ? <AvatarStack people={meeting.attendees.map((a) => ({ name: a.name ?? a.email, hue: a.hue }))} size="xs" /> : null}
            {upcoming ? <Chip tone="accent">{startsIn(meeting)}</Chip> : null}
            <span className="spacer" />
            {joinable(meeting) && meeting.meetUrl ? (
              <a className="btn btn-sm btn-primary" href={meeting.meetUrl} target="_blank" rel="noreferrer">
                <Icon name="video" />
                Join
              </a>
            ) : meeting.htmlLink ? (
              <a className="btn btn-sm btn-ghost" href={meeting.htmlLink} target="_blank" rel="noreferrer">
                <Icon name="external" />
                Open in Calendar
              </a>
            ) : null}
          </>
        ) : null
      }
    />
  );
}

/* ---------- everything else ---------- */

const CREATED_PHRASE: Record<string, string> = {
  Referral: 'Created from a referral',
  Event: 'Created from an event',
  'Website enquiry': 'Created from a website enquiry',
  'Shared inbox': 'Created from the shared inbox',
  'Forwarded email': 'Created from a forwarded email',
  'AI Finder': 'Found by the AI Finder',
  'LinkedIn outreach': 'Created from LinkedIn outreach',
  'Existing relationship': 'Added from an existing relationship',
};

function ItemCard({ item, sheet, fresh, onReply }: { item: TimelineItem; sheet: RecordSheet; fresh: boolean; onReply: (name: string) => void }) {
  switch (item.kind) {
    case 'note':
      return <NoteCard item={item} note={item.note} sheet={sheet} fresh={fresh} />;
    case 'email':
      return <EmailCard item={item} email={item.email} sheet={sheet} fresh={fresh} />;
    case 'meeting':
      return <MeetingCard item={item} meeting={item.meeting} fresh={fresh} />;
    case 'comment': {
      const c = item.comment;
      return (
        <Card
          itemId={item.id}
          fresh={fresh}
          icon="comment"
          tone="comment"
          t1={
            <>
              <b>Comment by {first(c.author, 'a former teammate')}</b>
              <span className="who">team only{c.editedAt ? ' · edited' : ''}</span>
              <Time at={item.at} />
            </>
          }
          t2={
            <span className="pre">
              <MentionText body={c.body} />
            </span>
          }
          t3={
            c.author ? (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => onReply(c.author!.name)}>
                <Icon name="reply" />
                Reply in thread
              </button>
            ) : null
          }
        />
      );
    }
    case 'file': {
      const f = item.file;
      const t = fileType(f.name, f.mime);
      return (
        <Card
          itemId={item.id}
          fresh={fresh}
          icon="file"
          t1={
            <>
              <b>File uploaded by {first(item.by)}</b>
              <Time at={item.at} />
            </>
          }
          t2={
            f.status === 'ready' ? (
              <a className="attach" href={`/api/files/${f.id}/download`} aria-label={`Download ${f.name}`}>
                <span className={`fi ${t.tone}`}>{t.label}</span>
                <span>
                  <b>{f.name}</b>
                  <span>{formatBytes(f.size)} · Amazon S3</span>
                </span>
              </a>
            ) : (
              <span className="attach">
                <span className={`fi ${t.tone}`}>{t.label}</span>
                <span>
                  <b>{f.name}</b>
                  <span>{f.status === 'uploading' ? 'Still uploading' : 'Upload did not finish'}</span>
                </span>
              </span>
            )
          }
        />
      );
    }
    case 'deal': {
      const e = item.event;
      const name = e.deal ? (
        <button type="button" className="link" onClick={() => actions.openDeal(e.deal!.id)}>
          {e.deal.name}
        </button>
      ) : (
        'A deleted deal'
      );
      const by = first(item.by);
      const value = e.value && e.currency ? formatMoney(e.value, e.currency) : null;
      if (e.kind === 'deal_moved')
        return (
          <Card
            itemId={item.id}
            fresh={fresh}
            icon="stage"
            tone="stage"
            t1={
              <>
                <b>Deal stage changed by {by}</b>
                <Time at={item.at} />
              </>
            }
            t2={
              <>
                {name}{' '}
                <span className="stage-move">
                  <span className={`dot ${e.fromColor ?? ''}`} />
                  {e.from ?? 'No stage'}
                  <Icon name="arrow-right" />
                  <span className={`dot ${e.toColor ?? ''}`} />
                  {e.to ?? 'No stage'}
                </span>
              </>
            }
          />
        );
      const title = e.kind === 'deal_created' ? `Deal created by ${by}` : e.kind === 'deal_won' ? `Deal won by ${by}` : `Deal lost, marked by ${by}`;
      const rest = [value, e.kind === 'deal_created' ? e.to : null, e.reason].filter(Boolean).join(' · ');
      return (
        <Card
          itemId={item.id}
          fresh={fresh}
          icon={e.kind === 'deal_created' ? 'deals' : e.kind === 'deal_won' ? 'trophy' : 'x-circle'}
          tone={e.kind === 'deal_won' ? 'won' : 'stage'}
          t1={
            <>
              <b>{title}</b>
              <Time at={item.at} />
            </>
          }
          t2={
            <>
              {name}
              {rest ? ` · ${rest}` : null}
            </>
          }
        />
      );
    }
    case 'followup': {
      const f = item.followup;
      return (
        <Card
          itemId={item.id}
          fresh={fresh}
          icon="clock"
          t1={
            <>
              <b>Follow-up added by {first(item.by)}</b>
              <span className="who">
                for {first(f.owner, 'nobody yet')} · {f.hasTime ? `${formatDay(f.dueAt)} ${formatTime(f.dueAt)}` : formatDay(f.dueAt)}
              </span>
              <Time at={item.at} />
            </>
          }
          t2={
            <>
              {f.title}
              {f.doneAt ? (
                <>
                  {' '}
                  <Chip tone="success" icon="check">
                    Done
                  </Chip>
                </>
              ) : null}
            </>
          }
        />
      );
    }
    case 'created': {
      const phrase = (item.source && CREATED_PHRASE[item.source]) ?? 'Added to Clientbook';
      const custom = item.source && !CREATED_PHRASE[item.source] && item.source !== 'Added manually' ? item.source : null;
      return (
        <Card
          itemId={item.id}
          fresh={fresh}
          icon={sheet.type === 'person' ? 'user-plus' : 'building'}
          t1={
            <>
              <b>{phrase}</b>
              <span className="who">{[item.by ? `by ${firstName(item.by.name)}` : null, custom].filter(Boolean).join(' · ')}</span>
              <Time at={item.at} />
            </>
          }
          t2={item.sourceDetail}
        />
      );
    }
    case 'ai': {
      const words = listWords(item.fields.map((f) => aiFieldWord(sheet.type, f)));
      return (
        <Card
          itemId={item.id}
          fresh={fresh}
          icon="sparkle"
          t1={
            <>
              <b>Details filled by AI</b>
              <span className="who">{modelName(item.model)}</span>
              <Time at={item.at} />
            </>
          }
          t2={words ? words.charAt(0).toUpperCase() + words.slice(1) : null}
        />
      );
    }
  }
}

export function Timeline({ sheet, onReply, focus }: { sheet: RecordSheet; onReply: (name: string) => void; focus?: string }) {
  const q = useTimeline(sheet.type, sheet.id);
  const fresh = useStore(freshStore, (s) => s.ids);
  const focused = useRef(false);

  useEffect(() => {
    if (!focus || focused.current || !q.data) return;
    const el = document.querySelector<HTMLElement>(`[data-item$="${CSS.escape(focus)}"]`);
    if (!el) return;
    focused.current = true;
    el.scrollIntoView({ block: 'center' });
    el.classList.add('is-focus');
  }, [focus, q.data]);

  if (q.isPending)
    return (
      <div className="tl" aria-busy="true">
        <Skeleton rows={5} />
      </div>
    );
  if (q.isError)
    return (
      <Callout
        tone="danger"
        title="The timeline did not load"
        actions={
          <button type="button" className="btn btn-sm btn-tile" onClick={() => void q.refetch()}>
            <Icon name="refresh" />
            Try again
          </button>
        }
      >
        {errorMessage(q.error)}
      </Callout>
    );

  // Upcoming meetings arrive soonest first; the timeline reads newest first, so the soonest sits just above today.
  const upcoming = [...(q.data.pages[0]?.upcoming ?? [])].reverse();
  const seen = new Set(upcoming.map((u) => u.id));
  const items = [...upcoming, ...q.data.pages.flatMap((p) => p.items).filter((i) => !seen.has(i.id))];
  if (items.length === 0)
    return (
      <Empty icon="activity" title="Nothing on the timeline yet" className="tl-empty">
        Notes, calls, emails and meetings with {sheet.type === 'person' ? firstName(sheet.name) : sheet.name} show up here.
      </Empty>
    );

  let lastDay = '';
  return (
    <div className="tl" aria-live="polite" aria-relevant="additions">
      {items.map((item) => {
        const day = dayHeading(item.at);
        const head = day !== lastDay;
        lastDay = day;
        return (
          <div key={item.id} className="tl-group">
            {head ? <div className="tl-day">{day}</div> : null}
            <ItemCard item={item} sheet={sheet} fresh={fresh.includes(item.id)} onReply={onReply} />
          </div>
        );
      })}
      {q.hasNextPage ? (
        <div className="tl-more">
          <button type="button" className="btn btn-sm btn-tile" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            {q.isFetchingNextPage ? <Spinner label="Loading older items" /> : <Icon name="chev-down" />}
            Show older
          </button>
        </div>
      ) : null}
    </div>
  );
}
